import React, { useCallback, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

import {
  Sheet,
  SheetBackdrop,
  SheetTitle,
} from "../../../../../components/MobileForm/styles";
import { useDropdownDismiss } from "../../../../../hooks/useDropdownDismiss";
import { ButtonTypes } from "../../../../../components/Button";
import {
  CheckboxFilter as CheckboxFilterContainer,
  CheckboxFilterClear,
  CheckboxFilterOption,
  CheckboxFilterPopover,
  CheckboxFilterSheetOptions,
  CheckboxFilterTrigger,
} from "./styles";
import { CheckboxFilterProps } from "./types";

const CheckboxFilterComponent = <T extends string>({
  options,
  selected,
  onChange,
  groupLabel,
  triggerLabelOf,
  isCompact = false,
  testId,
}: CheckboxFilterProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const popoverId = useId();

  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  useDropdownDismiss(containerRef, menuRef, isOpen, close);

  const toggle = (value: T) => {
    const next = new Set(selected);
    if (next.has(value)) {
      next.delete(value);
    } else {
      next.add(value);
    }
    onChange(
      options.flatMap((option) =>
        next.has(option.value) ? [option.value] : [],
      ),
    );
  };

  const triggerLabel = triggerLabelOf(selected);
  const menuContent = (
    <>
      {options.map((option) => (
        <CheckboxFilterOption key={option.value}>
          <input
            type="checkbox"
            checked={selected.includes(option.value)}
            disabled={option.disabled}
            onChange={() => toggle(option.value)}
          />
          {option.label}
        </CheckboxFilterOption>
      ))}
      <CheckboxFilterClear
        type={ButtonTypes.clear}
        size="small"
        disabled={selected.length === 0}
        onClick={() => onChange([])}
      >
        Clear
      </CheckboxFilterClear>
    </>
  );

  let menu: React.ReactNode = null;
  if (isOpen && isCompact) {
    menu = createPortal(
      <SheetBackdrop>
        <Sheet ref={menuRef}>
          <SheetTitle>{groupLabel}</SheetTitle>
          <CheckboxFilterSheetOptions
            id={popoverId}
            role="group"
            aria-label={groupLabel}
          >
            {menuContent}
          </CheckboxFilterSheetOptions>
        </Sheet>
      </SheetBackdrop>,
      document.body,
    );
  } else if (isOpen) {
    menu = (
      <CheckboxFilterPopover
        ref={menuRef}
        id={popoverId}
        role="group"
        aria-label={groupLabel}
      >
        {menuContent}
      </CheckboxFilterPopover>
    );
  }

  return (
    <CheckboxFilterContainer ref={containerRef} data-testid={testId}>
      <CheckboxFilterTrigger
        type="button"
        aria-label={`${groupLabel}: ${triggerLabel}`}
        aria-expanded={isOpen}
        aria-controls={popoverId}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {triggerLabel}
        <KeyboardArrowDownIcon />
      </CheckboxFilterTrigger>
      {menu}
    </CheckboxFilterContainer>
  );
};

// React.memo drops the component's type parameter, so the cast restores it.
export const CheckboxFilter = React.memo(
  CheckboxFilterComponent,
) as typeof CheckboxFilterComponent;
